import { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../App';
import { ConfirmModal } from './Modals';

// #207 parte 2 — botón «Rearmar honeypots». Sólo admin (el endpoint también lo
// exige) y sólo con un agente >= 1.19.4, que es el que conoce el comando.
export const HONEYPOT_REARM_MIN_AGENT_VERSION = '1.19.4';

const _parts = v => String(v).split('.').map(n => parseInt(n, 10));

export function supportsHoneypotRearm(version) {
  if (!version) return false;
  const a = _parts(version);
  if (a.some(Number.isNaN)) return false;
  const b = _parts(HONEYPOT_REARM_MIN_AGENT_VERSION);
  for (let i = 0; i < b.length; i++) {
    const x = a[i] || 0;
    if (x !== b[i]) return x > b[i];
  }
  return true;
}

const CONFIRM_TEXT =
  'Rearmar vuelve a crear los honeypots de este job y descarta la alerta actual. ' +
  'Usalo cuando sepas por qué cambiaron los honeypots (carpeta reorganizada, falsa alarma confirmada). ' +
  'Si no sabés por qué cambiaron, tratalo como un posible ransomware: no rearmes.';

// El polling dura ~3 min (120 consultas cada 1,5 s). El pedido en el backend vive
// 15 min: si el agente tarda más (reconectando), el resultado igual queda en la
// auditoría y en el indicador, y la consola no se queda consultando todo ese rato.
const MAX_POLLS = 120;
const RUNNING_REASON = 'Hay una corrida en curso de este job. Esperá a que termine para rearmar.';

export default function HoneypotRearmButton({ jobId, agentVersion, running = false, onChanged, pollMs = 1500, maxPolls = MAX_POLLS }) {
  const { isAdmin } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null); // { state: 'pending'|'ok'|'error', text }
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  if (!isAdmin) return null;

  const supported = supportsHoneypotRearm(agentVersion);
  // Agente viejo gana sobre «corriendo»: esperar no lo arregla.
  const available = supported && !running;
  const title = !supported
    ? `El agente (versión ${agentVersion || 'desconocida'}) no soporta el rearmado: se necesita ${HONEYPOT_REARM_MIN_AGENT_VERSION} o posterior`
    : running ? RUNNING_REASON : 'Vuelve a crear los honeypots de este job';

  const run = async () => {
    setConfirming(false);
    setResult({ state: 'pending', text: 'Rearmando…' });
    try {
      const { request_id } = await api.rearmHoneypots(jobId);
      for (let i = 0; i < maxPolls && alive.current; i++) {
        const r = await api.rearmHoneypotsStatus(jobId, request_id);
        if (!alive.current) return;
        if (r.status !== 'pending') {
          setResult(r.status === 'ok'
            ? { state: 'ok', text: r.detail || 'Honeypots rearmados' }
            : { state: 'error', text: r.detail || 'No se pudo rearmar' });
          if (onChanged) onChanged();
          return;
        }
        await new Promise(res => setTimeout(res, pollMs));
      }
      // No es un error: el pedido sigue abierto en el backend y puede contestar después.
      if (alive.current) setResult({ state: 'open', text: 'Sin respuesta todavía. El pedido sigue abierto: el resultado va a quedar en la auditoría y en el indicador.' });
    } catch (e) {
      if (alive.current) setResult({ state: 'error', text: e.message });
    }
  };

  const busy = !!result && result.state === 'pending';
  return (
    <>
      <button onClick={() => setConfirming(true)} disabled={!available || busy} title={title}
        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)',
                 padding: '3px 8px', fontSize: 10, cursor: available && !busy ? 'pointer' : 'not-allowed',
                 fontFamily: 'var(--mono)', opacity: available && !busy ? 1 : 0.5 }}>
        Rearmar honeypots
      </button>
      {result && (
        <span role="status" style={{ fontSize: 11, fontFamily: 'var(--mono)',
          color: result.state === 'ok' ? 'var(--green)' : result.state === 'error' ? 'var(--red)' : 'var(--muted)' }}>
          {result.state === 'ok' ? '✓ ' : result.state === 'error' ? '✕ ' : ''}{result.text}
        </span>
      )}
      {confirming && (
        <ConfirmModal title="Rearmar honeypots" message={CONFIRM_TEXT} danger
          onConfirm={run} onCancel={() => setConfirming(false)} />
      )}
    </>
  );
}
